import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { Switch } from '../components/ui/switch';
import { Plus, Edit2, Trash2, Save, Search, RefreshCw, Download, Upload, Eye, Copy, Archive, BarChart3, Tag, Folder, CheckCircle, XCircle, FileJson, FileText, Layers, Palette } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import CategoriesInfoDialogs from './CategoriesInfoDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Couleurs disponibles pour les catégories
const CATEGORY_COLORS = [
  { value: 'blue', label: 'Bleu', class: 'bg-blue-500' },
  { value: 'green', label: 'Vert', class: 'bg-green-500' },
  { value: 'amber', label: 'Ambre', class: 'bg-amber-500' },
  { value: 'red', label: 'Rouge', class: 'bg-red-500' },
  { value: 'purple', label: 'Violet', class: 'bg-purple-500' },
  { value: 'indigo', label: 'Indigo', class: 'bg-indigo-500' },
  { value: 'pink', label: 'Rose', class: 'bg-pink-500' },
  { value: 'teal', label: 'Sarcelle', class: 'bg-teal-500' },
  { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
  { value: 'slate', label: 'Ardoise', class: 'bg-slate-500' }
];

const CategoriesManagementPro = () => {
  const { getAuthHeader } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: 'blue',
    icon: 'folder',
    order: 0,
    is_active: true,
    parent_id: null,
    metadata: {}
  });
  
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    withNomenclatures: 0
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      fetchCategories();
    }
  }, [selectedCollection, showArchived]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/nomenclature-collections`, {
        headers: getAuthHeader()
      });
      setCollections(response.data);
      const active = response.data.find(c => c.is_active);
      if (active && !selectedCollection) {
        setSelectedCollection(active);
      }
    } catch {
      toast.error('Erreur lors du chargement des collections');
    }
  };

  const fetchCategories = async () => {
    if (!selectedCollection) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/categories?collection_id=${selectedCollection.id}${showArchived ? '&include_archived=true' : ''}`,
        { headers: getAuthHeader() }
      );
      setCategories(response.data);
      calculateStats(response.data);
    } catch {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      active: data.filter(c => c.is_active && !c.is_archived).length,
      archived: data.filter(c => c.is_archived).length,
      withNomenclatures: data.filter(c => (c.nomenclatures_count || 0) > 0).length
    });
  };

  const filteredCategories = categories.filter(c => {
    const matchSearch = !searchTerm || 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  const handleCreate = async () => {
    try {
      await axios.post(
        `${API}/categories`,
        { ...formData, collection_id: selectedCollection?.id },
        { headers: getAuthHeader() }
      );
      toast.success('Catégorie créée avec succès');
      setCreateDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    if (!currentCategory) return;
    try {
      await axios.put(
        `${API}/categories/${currentCategory.id}`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success('Catégorie mise à jour');
      setEditDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeader() });
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.patch(
        `${API}/categories/${id}/archive`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Catégorie archivée');
      fetchCategories();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleDuplicate = async (category) => {
    try {
      await axios.post(
        `${API}/categories`,
        {
          ...category,
          id: undefined,
          code: `${category.code}_COPY`,
          name: `${category.name} (copie)`,
          collection_id: selectedCollection?.id
        },
        { headers: getAuthHeader() }
      );
      toast.success('Catégorie dupliquée');
      fetchCategories();
    } catch {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleExport = (format) => {
    let content, filename, type;
    
    if (format === 'json') {
      content = JSON.stringify({ categories: filteredCategories, exported_at: new Date().toISOString() }, null, 2);
      filename = `categories_${selectedCollection?.name || 'export'}_${new Date().toISOString().split('T')[0]}.json`;
      type = 'application/json';
    } else {
      const headers = ['Code', 'Nom', 'Description', 'Couleur', 'Ordre', 'Actif'];
      const rows = filteredCategories.map(c => [
        c.code, c.name, c.description || '', c.color, c.order, c.is_active ? 'Oui' : 'Non'
      ]);
      content = [headers.join(';'), ...rows.map(r => r.map(v => `"${v}"`).join(';'))].join('\n');
      filename = `categories_${selectedCollection?.name || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
      type = 'text/csv;charset=utf-8;';
    }
    
    const blob = new Blob(['\uFEFF' + content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Export ${format.toUpperCase()} réussi !`);
    setExportDialogOpen(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      color: 'blue',
      icon: 'folder',
      order: categories.length,
      is_active: true,
      parent_id: null,
      metadata: {}
    });
    setCurrentCategory(null);
  };

  const openEditDialog = (category) => {
    setCurrentCategory(category);
    setFormData({
      name: category.name || '',
      code: category.code || '',
      description: category.description || '',
      color: category.color || 'blue',
      icon: category.icon || 'folder',
      order: category.order || 0,
      is_active: category.is_active !== false,
      parent_id: category.parent_id || null,
      metadata: category.metadata || {}
    });
    setEditDialogOpen(true);
  };

  const getColorClass = (color) => {
    return CATEGORY_COLORS.find(c => c.value === color)?.class || 'bg-blue-500';
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/30 min-h-screen">
      
      {/* Bouton Retour */}
      <BackButton label="Retour" />

{/* Header Professionnel */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Tag className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Gestion des Catégories</h1>
              <p className="text-white/80 text-sm">Organisez vos nomenclatures par catégories</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-xs text-white/70">Total</div>
              </div>
              <div className="bg-green-500/30 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.active}</div>
                <div className="text-xs text-white/70">Actives</div>
              </div>
              <div className="bg-blue-500/30 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.withNomenclatures}</div>
                <div className="text-xs text-white/70">Utilisées</div>
              </div>
            </div>
            
            <Button onClick={() => setStatsDialogOpen(true)} variant="ghost" className="text-white hover:bg-white/20">
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button onClick={fetchCategories} variant="ghost" className="text-white hover:bg-white/20">
              <RefreshCw className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Barre d'outils */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Select 
                value={selectedCollection?.id || ''} 
                onValueChange={(val) => setSelectedCollection(collections.find(c => c.id === val))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Collection" />
                </SelectTrigger>
                <SelectContent>
                  {collections.map(col => (
                    <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-[250px]"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch checked={showArchived} onCheckedChange={setShowArchived} />
                <Label className="text-sm text-slate-600">Archivées</Label>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" /> Importer
              </Button>
              <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)}>
                <Download className="h-4 w-4 mr-1" /> Exporter
              </Button>
              <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-1" /> Nouvelle catégorie
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grille des catégories */}
      {loading ? (
        <Card className="p-12 text-center">
          <RefreshCw className="h-8 w-8 mx-auto animate-spin text-emerald-600 mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </Card>
      ) : filteredCategories.length === 0 ? (
        <Card className="p-12 text-center">
          <Folder className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucune catégorie</h3>
          <p className="text-slate-500 mb-4">Créez votre première catégorie</p>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Créer une catégorie
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCategories.map((category) => (
            <Card 
              key={category.id} 
              className={`border-0 shadow-md hover:shadow-lg transition-all overflow-hidden ${category.is_archived ? 'opacity-60' : ''}`}
            >
              <div className={`h-2 ${getColorClass(category.color)}`} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getColorClass(category.color)} bg-opacity-20`}>
                      <Folder className={`h-5 w-5 text-${category.color}-600`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{category.name}</h3>
                      <Badge variant="outline" className="font-mono text-xs bg-slate-700 text-white border-slate-800 font-semibold">{category.code}</Badge>
                    </div>
                  </div>
                  <Badge className={category.is_active ? 'bg-green-700 text-white' : 'bg-slate-700 text-white'}>
                    {category.is_active ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                    {category.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
                
                {category.description && (
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3">{category.description}</p>
                )}
                
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                  <Layers className="h-3 w-3" />
                  <span>{category.nomenclatures_count || 0} nomenclature(s)</span>
                </div>
                
                <div className="flex items-center gap-1 pt-2 border-t">
                  <Button variant="ghost" size="sm" onClick={() => { setCurrentCategory(category); setViewDialogOpen(true); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(category)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDuplicate(category)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleArchive(category.id)}>
                    <Archive className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(category.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Création/Édition */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editDialogOpen ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editDialogOpen ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input 
                  value={formData.code} 
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="EX: CAT-01"
                />
              </div>
              <div className="space-y-2">
                <Label>Ordre</Label>
                <Input 
                  type="number"
                  value={formData.order} 
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nom de la catégorie"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Description..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Palette className="h-4 w-4" /> Couleur</Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_COLORS.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setFormData({...formData, color: color.value})}
                    className={`w-8 h-8 rounded-lg ${color.class} ${formData.color === color.value ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label>Catégorie active</Label>
              <Switch 
                checked={formData.is_active}
                onCheckedChange={(val) => setFormData({...formData, is_active: val})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>
              Annuler
            </Button>
            <Button onClick={editDialogOpen ? handleUpdate : handleCreate} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="h-4 w-4 mr-2" />
              {editDialogOpen ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Vue */}
      <CategoriesInfoDialogs
        viewDialogOpen={viewDialogOpen}
        setViewDialogOpen={setViewDialogOpen}
        exportDialogOpen={exportDialogOpen}
        setExportDialogOpen={setExportDialogOpen}
        statsDialogOpen={statsDialogOpen}
        setStatsDialogOpen={setStatsDialogOpen}
        currentCategory={currentCategory}
        stats={stats}
        handleExport={handleExport}
        getColorClass={getColorClass}
      />

      <input ref={fileInputRef} type="file" accept=".json,.csv" className="hidden" />
    </div>
  );
};

export default CategoriesManagementPro;
